import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { venueUrl } from "../../utils/venueUrl";
import { API, formatApiErrorDetail } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Search, Ban, Trash2, UserCheck, Edit, ClipboardList, ExternalLink, Building2, Upload, Loader2, Copy, Link as LinkIcon, Mail, Archive, ArchiveRestore, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

const DEFAULT_ROLES = [
  { value: "customer", label: "Customer" },
  { value: "venue_owner", label: "Venue Owner" },
  { value: "venue_agent", label: "Venue Agent" },
  { value: "platform_agent", label: "Platform Agent" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active"); // active | suspended | archived | all
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // { user, archive: bool }
  const [editingUser, setEditingUser] = useState(null);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [loginHistoryUser, setLoginHistoryUser] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [selected, setSelected] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'disable' | 'enable' | 'delete'
  const [bulkRunning, setBulkRunning] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/roles`)
      .then(res => { if (Array.isArray(res.data) && res.data.length) setRoles(res.data); })
      .catch(() => {});
  }, []);

  const fetchUsers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (roleFilter !== "all") params.set("role", roleFilter);
      if (statusFilter && statusFilter !== "active") params.set("status", statusFilter);
      const response = await axios.get(`${API}/admin/users?${params.toString()}`);
      setUsers(response.data.users || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchUsers();
  }, [search, roleFilter, statusFilter]);

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await axios.put(`${API}/admin/users/${archiveTarget.user.user_id}/archive`, {
        is_archived: archiveTarget.archive,
      });
      toast.success(archiveTarget.archive ? t("admin_users:toast_archived", { defaultValue: "User archived" }) : t("admin_users:toast_unarchived", { defaultValue: "User restored" }));
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("admin_users:toast_archive_failed", { defaultValue: "Failed to archive user" }));
    } finally {
      setArchiveTarget(null);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await axios.put(`${API}/admin/users/${user.user_id}/status`, {
        is_active: !user.is_active,
      });
      setUsers(users.map((u) => u.user_id === user.user_id ? { ...u, is_active: res.data.is_active } : u));
      toast.success(user.is_active ? t("admin_users:toast_disabled", { defaultValue: "User disabled" }) : t("admin_users:toast_enabled", { defaultValue: "User enabled" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin_users:toast_status_failed", { defaultValue: "Failed to update user status" }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/users/${deleteTarget.user_id}`);
      setUsers(users.filter((u) => u.user_id !== deleteTarget.user_id));
      setTotal((t) => t - 1);
      toast.success(t("admin_users:toast_deleted", { defaultValue: "User deleted" }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin_users:toast_delete_failed", { defaultValue: "Failed to delete user" }));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleUserUpdated = (updated) => {
    setUsers(users.map((u) => u.user_id === updated.user_id ? { ...u, ...updated } : u));
    setEditingUser(null);
  };

  const handleResendWelcome = async (user) => {
    if (!window.confirm(`Resend welcome email with a fresh 7-day set-password link to ${user.email}?`)) return;
    try {
      const res = await axios.post(`${API}/admin/users/${user.user_id}/resend-welcome`);
      toast.success("Welcome email resent", {
        description: res.data.magic_link ? "Magic link regenerated (valid 7 days)" : undefined,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend");
    }
  };

  const toggleSelect = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === users.length && users.length > 0) setSelected(new Set());
    else setSelected(new Set(users.map(u => u.user_id)));
  };

  const runBulkAction = async () => {
    if (!bulkAction || selected.size === 0) return;
    setBulkRunning(true);
    try {
      const userIds = Array.from(selected);
      if (bulkAction === "delete") {
        const res = await axios.post(`${API}/admin/users/bulk-delete`, { user_ids: userIds });
        toast.success(`Deleted ${res.data.deleted}, skipped ${res.data.skipped.length}`);
      } else if (bulkAction === "resend") {
        const res = await axios.post(`${API}/admin/users/bulk-resend-welcome`, { user_ids: userIds });
        toast.success(`Welcome email resent to ${res.data.sent}, skipped ${res.data.skipped.length}`);
      } else {
        const isActive = bulkAction === "enable";
        const res = await axios.post(`${API}/admin/users/bulk-status?is_active=${isActive}`, { user_ids: userIds });
        toast.success(`${isActive ? "Enabled" : "Disabled"} ${res.data.affected}, skipped ${res.data.skipped.length}`);
      }
      setSelected(new Set());
      await fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkRunning(false);
      setBulkAction(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-purple-100 text-purple-800",
      admin: "bg-blue-100 text-blue-800",
      venue_owner: "bg-green-100 text-green-800",
      venue_agent: "bg-teal-100 text-teal-800",
      platform_agent: "bg-indigo-100 text-indigo-800",
      customer: "bg-gray-100 text-gray-800",
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || "bg-gray-100"}`}>
        {role.replace(/_/g, " ")}
      </span>
    );
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground flex items-center gap-2"><Users className="h-6 w-6 text-primary" />{t("admin_users:title", { defaultValue: "Users" })} ({total})</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowBulkImport(true)} data-testid="open-bulk-import">
            <Upload className="h-4 w-4 mr-2" />
            {t("admin_users:bulk_import", { defaultValue: "Bulk Import" })}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowActivityLog(true)} data-testid="open-activity-log">
            <ClipboardList className="h-4 w-4 mr-2" />
            {t("admin_users:activity_log", { defaultValue: "Activity Log" })}
          </Button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-primary/10 border border-primary/30 rounded-xl mb-4 px-4 py-3 flex items-center justify-between" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-foreground">
            {t("admin_users:users_selected", { count: selected.size, defaultValue: `${selected.size} users selected` })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setBulkAction("enable")} data-testid="bulk-enable-btn">
              <UserCheck className="h-4 w-4 mr-1.5" /> {t("admin_users:enable", { defaultValue: "Enable" })}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkAction("disable")} data-testid="bulk-disable-btn">
              <Ban className="h-4 w-4 mr-1.5" /> {t("admin_users:disable", { defaultValue: "Disable" })}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkAction("resend")} data-testid="bulk-resend-btn">
              <Mail className="h-4 w-4 mr-1.5" /> {t("admin_users:resend_welcome", { defaultValue: "Resend Welcome" })}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setBulkAction("delete")} className="text-red-600 hover:text-red-700 border-red-200" data-testid="bulk-delete-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> {t("admin_users:delete", { defaultValue: "Delete" })}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">
              {t("admin_users:clear", { defaultValue: "Clear" })}
            </Button>
          </div>
        </div>
      )}

      <div className="bg-card rounded-xl border border-border mb-6 p-4">
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("admin_users:search_placeholder", { defaultValue: "Search by name or email..." })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="admin-user-search"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-44" data-testid="admin-user-role-filter">
              <SelectValue placeholder={t("admin_users:filter_by_role", { defaultValue: "Filter by role" })} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin_users:all_roles", { defaultValue: "All Roles" })}</SelectItem>
              {roles.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="admin-user-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">{t("admin_users:status_active", { defaultValue: "Active" })}</SelectItem>
              <SelectItem value="suspended">{t("admin_users:status_suspended", { defaultValue: "Suspended" })}</SelectItem>
              <SelectItem value="archived">{t("admin_users:status_archived", { defaultValue: "Archived" })}</SelectItem>
              <SelectItem value="all">{t("admin_users:status_all", { defaultValue: "All (incl. archived)" })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-muted h-16 rounded-lg" />
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <input
                    type="checkbox"
                    checked={users.length > 0 && selected.size === users.length}
                    onChange={toggleSelectAll}
                    className="rounded border-border cursor-pointer"
                    data-testid="bulk-select-all"
                    aria-label="Select all users"
                  />
                </TableHead>
                <TableHead>{t("admin_users:col_name", { defaultValue: "Name" })}</TableHead>
                <TableHead>{t("admin_users:col_email", { defaultValue: "Email" })}</TableHead>
                <TableHead>{t("admin_users:col_role", { defaultValue: "Role" })}</TableHead>
                <TableHead>{t("admin_users:col_linked_venues", { defaultValue: "Linked Venues" })}</TableHead>
                <TableHead>{t("admin_users:col_joined", { defaultValue: "Joined" })}</TableHead>
                <TableHead>{t("admin_users:col_status", { defaultValue: "Status" })}</TableHead>
                <TableHead>{t("admin_users:col_change_role", { defaultValue: "Change Role" })}</TableHead>
                <TableHead className="text-right">{t("admin_users:col_actions", { defaultValue: "Actions" })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id} data-testid={`admin-user-row-${user.user_id}`} className={`${selected.has(user.user_id) ? "bg-primary/5" : ""} ${user.is_archived ? "opacity-60" : ""}`}>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={selected.has(user.user_id)}
                      onChange={() => toggleSelect(user.user_id)}
                      className="rounded border-border cursor-pointer"
                      data-testid={`bulk-select-${user.user_id}`}
                      aria-label={`Select ${user.email}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{user.name || "\u2014"}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    <LinkedVenues venues={user.linked_venues} />
                  </TableCell>
                  <TableCell>{user.created_at ? format(new Date(user.created_at), "MMM d, yyyy") : "N/A"}</TableCell>
                  <TableCell>
                    {user.is_archived ? (
                      <span className="text-amber-700 text-sm font-medium bg-amber-50 px-2 py-0.5 rounded-full">{t("admin_users:status_archived", { defaultValue: "Archived" })}</span>
                    ) : user.is_active ? (
                      <span className="text-green-600 text-sm font-medium">{t("admin_users:status_active", { defaultValue: "Active" })}</span>
                    ) : (
                      <span className="text-red-600 text-sm font-medium">{t("admin_users:status_disabled", { defaultValue: "Disabled" })}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <RoleChanger
                      user={user}
                      roles={roles}
                      onUpdate={(updated) => setUsers(users.map((u) => u.user_id === updated.user_id ? { ...u, role: updated.role } : u))}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setEditingUser(user)}
                        title={t("admin_users:action_edit", { defaultValue: "Edit user" })}
                        data-testid={`user-edit-${user.user_id}`}
                      >
                        <Edit className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleToggleActive(user)}
                        title={user.is_active ? t("admin_users:action_disable", { defaultValue: "Disable user" }) : t("admin_users:action_enable", { defaultValue: "Enable user" })}
                        data-testid={`user-toggle-active-${user.user_id}`}
                      >
                        {user.is_active ? (
                          <Ban className="h-4 w-4 text-orange-500" />
                        ) : (
                          <UserCheck className="h-4 w-4 text-green-500" />
                        )}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleResendWelcome(user)}
                        title={t("admin_users:action_resend_welcome", { defaultValue: "Resend welcome email (fresh set-password link)" })}
                        data-testid={`user-resend-welcome-${user.user_id}`}
                      >
                        <Mail className="h-4 w-4 text-purple-500" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setLoginHistoryUser(user)}
                        title={t("admin_logins:view_login_history")}
                        data-testid={`user-login-history-${user.user_id}`}
                      >
                        <KeyRound className="h-4 w-4 text-blue-500" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setArchiveTarget({ user, archive: !user.is_archived })}
                        title={user.is_archived ? t("admin_users:action_unarchive", { defaultValue: "Restore from archive" }) : t("admin_users:action_archive", { defaultValue: "Archive user (hidden from default view)" })}
                        data-testid={`user-archive-${user.user_id}`}
                      >
                        {user.is_archived ? (
                          <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                        ) : (
                          <Archive className="h-4 w-4 text-amber-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => setDeleteTarget(user)}
                        title={t("admin_users:action_delete", { defaultValue: "Delete user" })}
                        data-testid={`user-delete-${user.user_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {t("admin_users:no_users", { defaultValue: "No users found." })}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("admin_users:edit_user", { defaultValue: "Edit User" })}</DialogTitle>
            <DialogDescription>{t("admin_users:edit_user_desc", { defaultValue: "Update user name, email, or phone number." })}</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <EditUserForm user={editingUser} onSuccess={handleUserUpdated} />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("admin_users:delete_dialog_title", { defaultValue: "Delete User" })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("admin_users:delete_dialog_desc", { defaultValue: "Are you sure you want to permanently delete this user? This action cannot be undone." })}
              <strong className="block mt-2 font-mono">{deleteTarget?.email}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">{t("admin_users:cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="user-delete-confirm">
              {t("admin_users:delete", { defaultValue: "Delete" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive / Unarchive Confirmation */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {archiveTarget?.archive
                ? t("admin_users:archive_dialog_title", { defaultValue: "Archive user?" })
                : t("admin_users:unarchive_dialog_title", { defaultValue: "Restore user?" })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget?.archive
                ? t("admin_users:archive_dialog_desc", { defaultValue: "Archived users are hidden from the default list and can't log in. You can restore them anytime via the Archived filter." })
                : t("admin_users:unarchive_dialog_desc", { defaultValue: "Restore this user — they will reappear in the active list and regain login access (unless also disabled)." })}
              <strong className="block mt-2 font-mono">{archiveTarget?.user.email}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-archive-cancel">{t("admin_users:cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} data-testid="user-archive-confirm">
              {archiveTarget?.archive
                ? t("admin_users:archive", { defaultValue: "Archive" })
                : t("admin_users:unarchive", { defaultValue: "Restore" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Action Confirmation */}
      <AlertDialog open={!!bulkAction} onOpenChange={(open) => !open && setBulkAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkAction === "delete" ? "Delete users?" : bulkAction === "disable" ? "Disable users?" : bulkAction === "resend" ? "Resend welcome emails?" : "Enable users?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === "delete"
                ? `Permanently delete ${selected.size} selected user${selected.size !== 1 ? "s" : ""}? This cannot be undone. Your own account and super_admin accounts (if you are not super_admin) will be skipped.`
                : bulkAction === "resend"
                ? `Send a fresh 7-day set-password magic link to ${selected.size} selected user${selected.size !== 1 ? "s" : ""}? Any previous unused links remain valid until they expire.`
                : `${bulkAction === "disable" ? "Disable" : "Enable"} ${selected.size} selected user${selected.size !== 1 ? "s" : ""}? Your own account will be skipped.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkRunning} data-testid="bulk-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runBulkAction}
              disabled={bulkRunning}
              className={bulkAction === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              data-testid="bulk-action-confirm"
            >
              {bulkRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {bulkAction === "delete" ? "Delete" : bulkAction === "disable" ? "Disable" : bulkAction === "resend" ? "Send emails" : "Enable"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Activity Log Dialog */}
      <Dialog open={showActivityLog} onOpenChange={setShowActivityLog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Admin Activity Log</DialogTitle>
            <DialogDescription>Track all admin actions on user accounts.</DialogDescription>
          </DialogHeader>
          {showActivityLog && <ActivityLog />}
        </DialogContent>
      </Dialog>

      {/* Per-user Login History Dialog */}
      <Dialog open={!!loginHistoryUser} onOpenChange={(open) => !open && setLoginHistoryUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="user-login-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              {t("admin_logins:view_login_history")}
            </DialogTitle>
            <DialogDescription>
              {loginHistoryUser?.name} · <span className="font-mono">{loginHistoryUser?.email}</span>
            </DialogDescription>
          </DialogHeader>
          {loginHistoryUser && <UserLoginHistory userId={loginHistoryUser.user_id} />}
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Import Users</DialogTitle>
            <DialogDescription>
              Upload a CSV file or paste JSON. Each row/object needs: name, email, role (customer, venue_owner, venue_agent, platform_agent, admin). Optional: phone. Temporary passwords are auto-generated.
            </DialogDescription>
          </DialogHeader>
          {showBulkImport && <BulkImportForm roles={roles} onDone={() => { setShowBulkImport(false); fetchUsers(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditUserForm({ user, onSuccess }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {};
      if (form.name !== (user.name || "")) payload.name = form.name;
      if (form.email !== (user.email || "")) payload.email = form.email;
      if (form.phone !== (user.phone || "")) payload.phone = form.phone;

      if (Object.keys(payload).length === 0) {
        toast.info(t("admin_users:toast_no_changes"));
        setLoading(false);
        return;
      }

      const res = await axios.put(`${API}/admin/users/${user.user_id}`, payload);
      toast.success(t("admin_users:toast_user_updated"));
      onSuccess(res.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || t("admin_users:toast_update_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="edit-user-form">
      <div>
        <Label>{t("admin_users:col_name")}</Label>
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1"
          data-testid="edit-user-name"
        />
      </div>
      <div>
        <Label>{t("admin_users:col_email")}</Label>
        <Input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="mt-1"
          data-testid="edit-user-email"
        />
      </div>
      <div>
        <Label>{t("admin_users:form_phone")}</Label>
        <Input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="mt-1"
          placeholder="+357..."
          data-testid="edit-user-phone"
        />
      </div>
      <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
        <span>{t("admin_users:col_role")}: <strong>{user.role.replace(/_/g, " ")}</strong></span>
        <span>|</span>
        <span>{t("admin_users:col_status")}: <strong>{user.is_active ? t("admin_users:status_active") : t("admin_users:status_disabled")}</strong></span>
      </div>
      <Button type="submit" className="w-full" disabled={loading} data-testid="edit-user-submit">
        {loading ? t("admin_users:form_saving") : t("admin_users:form_save")}
      </Button>
    </form>
  );
}

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/activity-log?limit=100`)
      .then((res) => {
        setLogs(res.data.logs || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error("Failed to load activity log"))
      .finally(() => setLoading(false));
  }, []);

  const getActionLabel = (action) => {
    const labels = {
      role_changed: "Changed Role",
      user_disabled: "Disabled User",
      user_enabled: "Enabled User",
      user_deleted: "Deleted User",
      user_edited: "Edited User",
    };
    return labels[action] || action;
  };

  const getActionColor = (action) => {
    const colors = {
      role_changed: "bg-blue-100 text-blue-800",
      user_disabled: "bg-orange-100 text-orange-800",
      user_enabled: "bg-green-100 text-green-800",
      user_deleted: "bg-red-100 text-red-800",
      user_edited: "bg-purple-100 text-purple-800",
    };
    return colors[action] || "bg-gray-100 text-gray-800";
  };

  const formatDetails = (action, details) => {
    if (action === "role_changed") {
      return `${(details.old_role || "").replace(/_/g, " ")} \u2192 ${(details.new_role || "").replace(/_/g, " ")}`;
    }
    if (action === "user_edited") {
      return `Updated: ${(details.fields_updated || []).join(", ")}`;
    }
    if (action === "user_disabled" || action === "user_enabled") {
      return details.is_active ? "Set to active" : "Set to disabled";
    }
    if (action === "user_deleted") {
      return `Role was: ${(details.role || "").replace(/_/g, " ")}`;
    }
    return "";
  };

  if (loading) {
    return <div className="space-y-2">{[...Array(5)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-12 rounded-lg" />)}</div>;
  }

  if (logs.length === 0) {
    return <p className="text-center text-muted-foreground py-8">No activity recorded yet.</p>;
  }

  return (
    <div data-testid="activity-log-list">
      <p className="text-sm text-muted-foreground mb-4">{total} total entries</p>
      <div className="space-y-3">
        {logs.map((log) => (
          <div key={log.log_id} className="flex items-start gap-3 p-3 bg-muted/40 rounded-lg" data-testid={`activity-log-${log.log_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                  {getActionLabel(log.action)}
                </span>
                <span className="text-sm font-medium text-foreground truncate">{log.target_email}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {formatDetails(log.action, log.details)}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                By <span className="font-medium">{log.admin_email}</span> on {log.created_at ? format(new Date(log.created_at), "MMM d, yyyy 'at' HH:mm") : "N/A"}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LinkedVenues({ venues }) {
  if (!venues || venues.length === 0) {
    return <span className="text-muted-foreground text-sm">{"\u2014"}</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {venues.map((v) => (
        <div key={v.venue_id} className="flex items-center gap-1.5" data-testid={`linked-venue-${v.venue_id}`}>
          <Building2 className="h-3.5 w-3.5 text-primary shrink-0" />
          <Link
            to={venueUrl(v)}
            target="_blank"
            className="text-sm text-primary hover:underline truncate max-w-[140px]"
            title={v.name}
          >
            {v.name}
          </Link>
          <Link
            to={`/admin/venues/${v.venue_id}/edit`}
            className="shrink-0"
            title="Edit venue"
          >
            <Edit className="h-3.5 w-3.5 text-muted-foreground hover:text-primary transition-colors" />
          </Link>
        </div>
      ))}
    </div>
  );
}

function RoleChanger({ user, roles, onUpdate }) {
  const [changing, setChanging] = useState(false);
  const rolesToShow = (roles && roles.length ? roles : [
    { value: "customer", label: "Customer" },
    { value: "venue_owner", label: "Venue Owner" },
    { value: "venue_agent", label: "Venue Agent" },
    { value: "platform_agent", label: "Platform Agent" },
    { value: "admin", label: "Admin" },
    { value: "super_admin", label: "Super Admin" },
  ]);

  const handleChange = async (newRole) => {
    if (newRole === user.role) return;
    setChanging(true);
    try {
      const res = await axios.put(`${API}/admin/users/${user.user_id}/role`, { role: newRole });
      onUpdate(res.data);
      toast.success(`Role updated to ${newRole.replace(/_/g, " ")}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setChanging(false);
    }
  };

  return (
    <Select value={user.role} onValueChange={handleChange} disabled={changing}>
      <SelectTrigger className="w-36 h-8" data-testid={`role-select-${user.user_id}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {rolesToShow.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}


function BulkImportForm({ roles, onDone }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState("csv"); // csv | json
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [parseError, setParseError] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const validRoles = new Set(roles.filter(r => r.value !== "super_admin").map(r => r.value));

  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return [];
    const header = lines[0].split(",").map(h => h.trim().toLowerCase());
    const nameIdx = header.indexOf("name");
    const emailIdx = header.indexOf("email");
    const roleIdx = header.indexOf("role");
    const phoneIdx = header.indexOf("phone");
    if (nameIdx === -1 || emailIdx === -1 || roleIdx === -1) {
      throw new Error("CSV header must include: name, email, role (phone optional)");
    }
    return lines.slice(1).map((line, idx) => {
      const cells = line.split(",").map(c => c.trim());
      return {
        __row: idx + 2,
        name: cells[nameIdx] || "",
        email: cells[emailIdx] || "",
        role: (cells[roleIdx] || "customer").toLowerCase(),
        phone: phoneIdx >= 0 ? (cells[phoneIdx] || "") : "",
      };
    }).filter(r => r.email);
  };

  const parseJson = (text) => {
    const obj = JSON.parse(text);
    const arr = Array.isArray(obj) ? obj : obj.users;
    if (!Array.isArray(arr)) throw new Error("JSON must be an array or {users: [...]}");
    return arr.map((r, idx) => ({
      __row: idx + 1,
      name: r.name || "",
      email: (r.email || "").toLowerCase(),
      role: (r.role || "customer").toLowerCase(),
      phone: r.phone || "",
    }));
  };

  const handlePreview = () => {
    setParseError("");
    setResult(null);
    try {
      const rows = mode === "csv" ? parseCsv(rawText) : parseJson(rawText);
      if (rows.length === 0) throw new Error("No rows to import");
      setParsed(rows);
    } catch (e) {
      setParseError(e.message || "Parse failed");
      setParsed([]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    setMode("csv");
  };

  const handleImport = async () => {
    if (parsed.length === 0) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-import`, {
        users: parsed.map(r => ({ name: r.name, email: r.email, role: r.role, phone: r.phone || null })),
        send_welcome_email: sendEmail,
      });
      setResult(res.data);
      toast.success(t("admin_users:bulk_import_summary", { created: res.data.created_count, skipped: res.data.skipped_count, errors: res.data.error_count }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t("admin_users:bulk_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCreds = () => {
    if (!result?.created?.length) return;
    const text = result.created.map(c => `${c.email}\t${c.magic_link || c.temp_password || ""}`).join("\n");
    navigator.clipboard.writeText(text);
    toast.success(t("admin_users:bulk_copied"));
  };

  if (result) {
    return (
      <div className="space-y-4" data-testid="bulk-import-result">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-semibold text-emerald-700">{result.created_count}</p>
            <p className="text-xs text-emerald-700">{t("admin_users:bulk_created")}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-semibold text-amber-700">{result.skipped_count}</p>
            <p className="text-xs text-amber-700">{t("admin_users:bulk_skipped")}</p>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
            <p className="text-2xl font-semibold text-red-700">{result.error_count}</p>
            <p className="text-xs text-red-700">{t("admin_users:bulk_errors")}</p>
          </div>
        </div>
        {result.created?.length > 0 && (
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/20">
              <span className="text-sm font-medium">{t("admin_users:bulk_created_users")}</span>
              <Button variant="ghost" size="sm" onClick={copyCreds} data-testid="copy-creds-btn">
                <Copy className="h-3.5 w-3.5 mr-1" /> {t("admin_users:bulk_copy_all")}
              </Button>
            </div>
            <div className="max-h-56 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>{t("admin_users:col_email")}</TableHead><TableHead>{t("admin_users:bulk_col_set_password_link")}</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {result.created.map(c => (
                    <TableRow key={c.user_id}>
                      <TableCell className="text-sm">{c.email}</TableCell>
                      <TableCell>
                        {c.magic_link ? (
                          <a href={c.magic_link} target="_blank" rel="noreferrer" className="text-xs text-primary underline break-all inline-flex items-center gap-1">
                            <LinkIcon className="h-3 w-3 shrink-0" /> {c.magic_link}
                          </a>
                        ) : (
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{c.temp_password}</code>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
        {(result.skipped?.length > 0 || result.errors?.length > 0) && (
          <div className="text-xs text-muted-foreground space-y-1">
            {result.skipped.map((s, i) => <div key={`s-${i}`}>Row {s.row} · {s.email}: {s.reason}</div>)}
            {result.errors.map((e, i) => <div key={`e-${i}`} className="text-red-600">Row {e.row} · {e.email}: {e.reason}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setResult(null); setParsed([]); setRawText(""); }} data-testid="bulk-import-reset">{t("admin_users:bulk_import_more")}</Button>
          <Button onClick={onDone} data-testid="bulk-import-close">{t("admin_users:bulk_close")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant={mode === "csv" ? "default" : "outline"} size="sm" onClick={() => setMode("csv")} data-testid="bulk-import-mode-csv">CSV</Button>
        <Button variant={mode === "json" ? "default" : "outline"} size="sm" onClick={() => setMode("json")} data-testid="bulk-import-mode-json">JSON</Button>
        {mode === "csv" && (
          <label className="ml-auto">
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={e => handleFileUpload(e.target.files?.[0])} data-testid="bulk-import-file-input" />
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted cursor-pointer">
              <Upload className="h-3.5 w-3.5" /> {t("admin_users:bulk_upload_csv")}
            </span>
          </label>
        )}
      </div>

      <div>
        <Label className="text-xs text-muted-foreground">
          {mode === "csv" ? t("admin_users:bulk_csv_label") : t("admin_users:bulk_json_label")}
        </Label>
        <textarea
          value={rawText}
          onChange={e => setRawText(e.target.value)}
          rows={8}
          className="w-full mt-1 p-3 text-sm font-mono border border-border rounded-md bg-background"
          placeholder={mode === "csv"
            ? "name,email,role,phone\nJohn Doe,john@example.com,customer,+357 99 123456\nJane Owner,jane@example.com,venue_owner,"
            : '[\n  {"name":"John Doe","email":"john@example.com","role":"customer"},\n  {"name":"Jane Owner","email":"jane@example.com","role":"venue_owner","phone":"+357 99"}\n]'}
          data-testid="bulk-import-textarea"
        />
      </div>

      {parseError && <div className="bg-destructive/10 text-destructive px-3 py-2 rounded-md text-sm">{parseError}</div>}

      {parsed.length > 0 && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-border bg-muted/20 text-sm font-medium">
            {t("admin_users:bulk_preview_count", { count: parsed.length })}
          </div>
          <div className="max-h-56 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow><TableHead>{t("admin_users:col_name")}</TableHead><TableHead>{t("admin_users:col_email")}</TableHead><TableHead>{t("admin_users:col_role")}</TableHead><TableHead>{t("admin_users:form_phone")}</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {parsed.map((r, i) => {
                  const invalid = !r.email.includes("@") || !validRoles.has(r.role);
                  return (
                    <TableRow key={i} className={invalid ? "bg-red-50" : ""}>
                      <TableCell className="text-sm">{r.name || <span className="text-muted-foreground italic">—</span>}</TableCell>
                      <TableCell className="text-sm">{r.email}</TableCell>
                      <TableCell className="text-sm">
                        <span className={validRoles.has(r.role) ? "" : "text-red-600 font-medium"}>{r.role}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{r.phone || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bulk-import-email-toggle">
        <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)} />
        {t("admin_users:bulk_send_email_label")}
      </label>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={handlePreview} disabled={!rawText.trim()} data-testid="bulk-import-preview">
          {t("admin_users:bulk_preview")}
        </Button>
        <Button onClick={handleImport} disabled={parsed.length === 0 || submitting} data-testid="bulk-import-submit">
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
          {t("admin_users:bulk_import_btn")} {parsed.length > 0 ? t("admin_users:bulk_import_count", { count: parsed.length }) : ""}
        </Button>
      </div>
    </div>
  );
}


function UserLoginHistory({ userId }) {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/analytics/logins?user_id=${userId}&limit=50`)
      .then(res => {
        setEvents(res.data.events || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error("Failed to load login history"))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="space-y-2 pt-2">
        {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-muted/50 rounded animate-pulse" />)}
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">{t("admin_logins:no_login_history")}</p>;
  }

  const fmtRelative = (iso) => {
    const diff = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
    const abs = Math.abs(diff);
    try {
      const rtf = new Intl.RelativeTimeFormat(i18n.language === "el" ? "el" : "en", { numeric: "auto" });
      if (abs < 60) return rtf.format(diff, "second");
      if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
      if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
      if (abs < 2592000) return rtf.format(Math.round(diff / 86400), "day");
      return rtf.format(Math.round(diff / 2592000), "month");
    } catch { return new Date(iso).toLocaleString(); }
  };

  return (
    <div className="pt-2">
      <p className="text-xs text-muted-foreground mb-3">Total: <span className="font-medium text-foreground">{total}</span></p>
      <div className="space-y-2" data-testid="user-login-history-list">
        {events.map((e) => {
          const providerColor = e.provider === "google" ? "bg-blue-100 text-blue-700" : e.provider === "invitation" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-700";
          return (
            <div key={e.event_id} className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg text-sm">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${e.success ? "bg-emerald-500" : "bg-rose-500"}`} />
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${providerColor}`}>
                {e.provider}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">
                  {e.success ? t("admin_logins:success") : t("admin_logins:failed")}
                  {!e.success && e.failure_reason && (
                    <span className="text-xs text-muted-foreground ml-2">
                      · {t(`admin_logins.reason.${e.failure_reason}`, { defaultValue: e.failure_reason })}
                    </span>
                  )}
                </p>
                <p className="text-[11px] text-muted-foreground font-mono">{e.ip || "—"}</p>
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap" title={e.created_at}>{fmtRelative(e.created_at)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
